import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import Logo from "../../assets/Doctor-AI_LOGO.png";
import { signUp } from "../../services/oprations/authAPI"; 

const Signup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Form states
  const [formData, setFormData] = useState({
    role: "Patient", // Default role updated to Patient
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  // UI & Validation states
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [errors, setErrors] = useState({});

  const { role, name, email, password, confirmPassword } = formData;

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear specific error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSignup = (e) => {
    e.preventDefault();
    const newErrors = {};

    // Form Validations
    if (!name.trim()) newErrors.name = "Full Name is required";
    
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    // If there are errors, set them and abort submission
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Dispatch the provided signUp action
    dispatch(signUp(role, name, email, password, confirmPassword, navigate));
  };

  return (
    <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-purple-600 min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Floating Background Shapes */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
        <div 
          className="absolute top-60 right-10 w-32 h-32 bg-white/10 rounded-full animate-bounce" 
          style={{ animationDelay: "2s", animationDuration: "6s" }}
        ></div>
        <div 
          className="absolute bottom-20 left-20 w-16 h-16 bg-white/10 rounded-full animate-ping" 
          style={{ animationDelay: "4s", animationDuration: "4s" }}
        ></div>
      </div>

      {/* Main Container */}
      <div className="w-full max-w-md bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/30 overflow-hidden relative my-8">
        
        {/* Header Section */}
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-8 text-center text-white relative">
          <div className="absolute -bottom-5 left-0 right-0 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 transform scale-x-125 rounded-b-[50%]"></div>
          <div className="relative z-10 flex flex-col items-center">
            <div className="w-48 h-12 flex items-center justify-center mb-2">
              <img src={Logo} alt="Doctor ai logo" className="max-h-full object-contain" />
            </div>
            <p className="text-indigo-100 text-sm font-medium">Create your account to get started</p>
          </div>
        </div>

        {/* Form Section */}
        <div className="p-8 pt-10">
          <form onSubmit={handleSignup} className="space-y-4">
            
            {/* Role Selection */}
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-700">Select Role</label>
              <select
                name="role"
                value={role}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border-2 border-indigo-200 bg-white/90 rounded-xl transition-all duration-300 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 text-gray-700 transform focus:-translate-y-0.5"
              >
                {/* Updated Options */}
                <option value="Patient">Patient</option>
                <option value="Doctor">Doctor</option>
              </select>
            </div>

            {/* Name Field */}
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-700">Full Name</label>
              <input
                type="text"
                name="name"
                value={name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                className={`w-full px-4 py-3 border-2 ${
                  errors.name ? "border-red-300 bg-red-50" : "border-indigo-200 bg-white/90"
                } rounded-xl transition-all duration-300 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transform focus:-translate-y-0.5`}
              />
              {errors.name && (
                <div className="flex items-center gap-2 text-xs text-red-500 animate-pulse mt-1">
                  <AlertCircle size={14} />
                  <span>{errors.name}</span>
                </div>
              )}
            </div>

            {/* Email Field */}
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-700">Email Address</label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                className={`w-full px-4 py-3 border-2 ${
                  errors.email ? "border-red-300 bg-red-50" : "border-indigo-200 bg-white/90"
                } rounded-xl transition-all duration-300 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transform focus:-translate-y-0.5`}
              />
              {errors.email && (
                <div className="flex items-center gap-2 text-xs text-red-500 animate-pulse mt-1">
                  <AlertCircle size={14} />
                  <span>{errors.email}</span>
                </div>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-700">Password</label>
              <div className="relative">
                <input
                  type={isPasswordVisible ? "text" : "password"}
                  name="password"
                  value={password}
                  onChange={handleInputChange}
                  placeholder="Create a password"
                  className={`w-full px-4 py-3 pr-12 border-2 ${
                    errors.password ? "border-red-300 bg-red-50" : "border-indigo-200 bg-white/90"
                  } rounded-xl transition-all duration-300 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transform focus:-translate-y-0.5`}
                />
                <button
                  type="button"
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-indigo-500 transition-colors duration-200"
                >
                  {isPasswordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <div className="flex items-center gap-2 text-xs text-red-500 animate-pulse mt-1">
                  <AlertCircle size={14} />
                  <span>{errors.password}</span>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-700">Confirm Password</label>
              <div className="relative">
                <input
                  type={isConfirmPasswordVisible ? "text" : "password"}
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Re-enter your password"
                  className={`w-full px-4 py-3 pr-12 border-2 ${
                    errors.confirmPassword ? "border-red-300 bg-red-50" : "border-indigo-200 bg-white/90"
                  } rounded-xl transition-all duration-300 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transform focus:-translate-y-0.5`}
                />
                <button
                  type="button"
                  onClick={() => setIsConfirmPasswordVisible(!isConfirmPasswordVisible)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-indigo-500 transition-colors duration-200"
                >
                  {isConfirmPasswordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <div className="flex items-center gap-2 text-xs text-red-500 animate-pulse mt-1">
                  <AlertCircle size={14} />
                  <span>{errors.confirmPassword}</span>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-4 px-6 rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-3 relative overflow-hidden group mt-6"
            >
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent transform skew-x-12"></div>
              Create Account
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center text-sm text-gray-600">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-indigo-600 hover:text-indigo-500 transition-colors">
              Sign In
            </Link>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default Signup;